import React, { useState, useEffect } from "react";
import { ADMIN_API_BASE_URL, currentAppMode } from "../config";
import { formatCurrency } from "../utils/formatters";

// Import icons individually to avoid TypeScript issues
import { FaTimes } from "react-icons/fa";
import { FaExclamationTriangle } from "react-icons/fa";
import { FaUpload } from "react-icons/fa";
import { FaEye } from "react-icons/fa";
import { FaEyeSlash } from "react-icons/fa";
import { FaSave } from "react-icons/fa";
import { handleApiResponse } from "../utils/commonfunction";

interface Banner {
  id: string;
  internal_name: string;
  headline: string;
  sub_headline: string;
  cta_text: string;
  image_url: string;
  status: "ACTIVE" | "INACTIVE";
  link_type: "CHALLENGE" | "CUSTOM_URL";
  link_target: string;
  display_order: number;
  appType: string;
  created_at: string;
  updated_at: string;
}

interface Challenge {
  id: string;
  name: string;
  entry_fee: number;
  status: string;
}

interface BannerFormProps {
  banner?: Banner;
  onSave: (
    banner: Omit<Banner, "id" | "created_at" | "updated_at" | "display_order">
  ) => void;
  onCancel: () => void;
  loading?: boolean;
  error?: string;
}

const BannerForm: React.FC<BannerFormProps> = ({
  banner,
  onSave,
  onCancel,
  loading = false,
  error,
}) => {
  const isEditing = !!banner;

  const [formData, setFormData] = useState<
    Omit<Banner, "id" | "created_at" | "updated_at" | "display_order">
  >({
    internal_name: banner?.internal_name || "",
    headline: banner?.headline || "",
    sub_headline: banner?.sub_headline || "",
    cta_text: banner?.cta_text || "",
    image_url: banner?.image_url || "",
    status: banner?.status || "ACTIVE",
    link_type: banner?.link_type || "CUSTOM_URL",
    link_target: banner?.link_target || "",
    appType: banner?.appType || currentAppMode,
  });

  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loadingChallenges, setLoadingChallenges] = useState(false);
  const [validationError, setValidationError] = useState("");

  // Fetch challenges when component mounts or link_type changes to CHALLENGE
  useEffect(() => {
    if (formData.link_type === "CHALLENGE") {
      fetchChallenges();
    }
  }, [formData.link_type]);

  const fetchChallenges = async () => {
    setLoadingChallenges(true);
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${ADMIN_API_BASE_URL}/challenges?appType=${currentAppMode}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setChallenges(data.challenges || []);
      } else {
        return handleApiResponse(response);
        console.error("Failed to fetch challenges");
      }
    } catch (error) {
      console.error("Error fetching challenges:", error);
    } finally {
      setLoadingChallenges(false);
    }
  };

  const handleInputChange = (
    field: keyof typeof formData,
    value: string | "ACTIVE" | "INACTIVE" | "CHALLENGE" | "CUSTOM_URL"
  ) => {
    setValidationError("");

    // Clear link target when link type changes
    if (field === "link_type") {
      setFormData((prev) => ({
        ...prev,
        [field]: value as "CHALLENGE" | "CUSTOM_URL",
        link_target: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // For now, we'll use a placeholder URL
      // In a real implementation, you'd upload to a cloud service
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData((prev) => ({
          ...prev,
          image_url: e.target?.result as string,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = (): boolean => {
    if (!formData.internal_name.trim()) {
      setValidationError("Internal name is required");
      return false;
    }
    if (!formData.headline.trim()) {
      setValidationError("Headline is required");
      return false;
    }
    if (!formData.cta_text.trim()) {
      setValidationError("CTA text is required");
      return false;
    }
    if (!formData.image_url.trim()) {
      setValidationError("Image is required");
      return false;
    }
    if (formData.link_type === "CHALLENGE" && !formData.link_target) {
      setValidationError("Please select a challenge");
      return false;
    }
    if (formData.link_type === "CUSTOM_URL" && !formData.link_target.trim()) {
      setValidationError("Custom URL is required");
      return false;
    }
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? "Edit Banner" : "Create New Banner"}
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <FaTimes className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <FaExclamationTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Internal Name */}
          <div>
            <label
              htmlFor="internal_name"
              className="block text-sm font-medium text-gray-700"
            >
              Internal Name *
            </label>
            <input
              type="text"
              id="internal_name"
              value={formData.internal_name}
              onChange={(e) =>
                handleInputChange("internal_name", e.target.value)
              }
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Summer Sale Banner"
              required
            />
            <p className="mt-1 text-sm text-gray-500">
              Internal name for admin reference (not visible to users)
            </p>
          </div>

          {/* Headline */}
          <div>
            <label
              htmlFor="headline"
              className="block text-sm font-medium text-gray-700"
            >
              Headline *
            </label>
            <input
              type="text"
              id="headline"
              value={formData.headline}
              onChange={(e) => handleInputChange("headline", e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Win Big This Summer!"
              required
            />
          </div>

          {/* Sub-headline */}
          <div>
            <label
              htmlFor="sub_headline"
              className="block text-sm font-medium text-gray-700"
            >
              Sub-headline
            </label>
            <input
              type="text"
              id="sub_headline"
              value={formData.sub_headline}
              onChange={(e) =>
                handleInputChange("sub_headline", e.target.value)
              }
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Join exciting challenges and win amazing prizes"
            />
          </div>

          {/* CTA Text */}
          <div>
            <label
              htmlFor="cta_text"
              className="block text-sm font-medium text-gray-700"
            >
              Call-to-Action Text *
            </label>
            <input
              type="text"
              id="cta_text"
              value={formData.cta_text}
              onChange={(e) => handleInputChange("cta_text", e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Play Now"
              required
            />
          </div>

          {/* Link Type */}
          <div>
            <label
              htmlFor="link_type"
              className="block text-sm font-medium text-gray-700"
            >
              Link Type *
            </label>
            <select
              id="link_type"
              value={formData.link_type}
              onChange={(e) =>
                handleInputChange(
                  "link_type",
                  e.target.value as "CHALLENGE" | "CUSTOM_URL"
                )
              }
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="CUSTOM_URL">Custom URL</option>
              <option value="CHALLENGE">Challenge</option>
            </select>
          </div>

          {/* Link Target */}
          <div>
            <label
              htmlFor="link_target"
              className="block text-sm font-medium text-gray-700"
            >
              {formData.link_type === "CHALLENGE"
                ? "Select Challenge *"
                : "Custom URL *"}
            </label>
            {formData.link_type === "CHALLENGE" ? (
              <select
                id="link_target"
                value={formData.link_target}
                onChange={(e) =>
                  handleInputChange("link_target", e.target.value)
                }
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={loadingChallenges}
                required
              >
                <option value="">Select a challenge...</option>
                {challenges.map((challenge) => (
                  <option key={challenge.id} value={challenge.id}>
                    {challenge.name} (₹{formatCurrency(challenge.entry_fee)}) -{" "}
                    {challenge.status}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="url"
                id="link_target"
                value={formData.link_target}
                onChange={(e) =>
                  handleInputChange("link_target", e.target.value)
                }
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://example.com"
                required
              />
            )}
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Banner Image *
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                {formData.image_url ? (
                  <div className="mb-4">
                    <img
                      src={formData.image_url}
                      alt="Banner preview"
                      className="mx-auto h-32 w-auto object-contain rounded"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, image_url: "" }))
                      }
                      className="mt-2 text-sm text-red-600 hover:text-red-500"
                    >
                      Remove image
                    </button>
                  </div>
                ) : (
                  <>
                    <FaUpload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="image-upload"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                      >
                        <FaUpload className="w-4 h-4 mr-2" />
                        Upload Image
                      </label>
                      <input
                        id="image-upload"
                        name="image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="sr-only"
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="status"
                  value="ACTIVE"
                  checked={formData.status === "ACTIVE"}
                  onChange={(e) =>
                    handleInputChange(
                      "status",
                      e.target.value as "ACTIVE" | "INACTIVE"
                    )
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="text-sm text-gray-700">Active</span>
                <FaEye className="w-4 h-4 ml-1 text-green-600" />
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="status"
                  value="INACTIVE"
                  checked={formData.status === "INACTIVE"}
                  onChange={(e) =>
                    handleInputChange(
                      "status",
                      e.target.value as "ACTIVE" | "INACTIVE"
                    )
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="text-sm text-gray-700">Inactive</span>
                <FaEyeSlash className="w-4 h-4 ml-1 text-gray-600" />
              </label>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Active banners will be displayed on the homepage
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaSave className="w-4 h-4 mr-2" />
              {loading
                ? "Saving..."
                : isEditing
                ? "Update Banner"
                : "Create Banner"}
            </button>
          </div>

          {validationError && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex">
                <FaExclamationTriangle className="h-5 w-5 text-yellow-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Validation Error
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    {validationError}
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default BannerForm;
